import { pipe } from "effect/Function";
import * as Arr from "effect/Array";
import * as O from "effect/Order";
export const DEFAULT_THREAD_DETAIL_LIMITS = {
    maxMessages: 512,
    maxActivities: 128,
};
const activityOrder = O.combineAll([
    O.mapInput(O.Number, (a) => a.sequence ?? Number.MAX_SAFE_INTEGER),
    O.mapInput(O.String, (a) => a.createdAt),
    O.mapInput(O.String, (a) => a.id),
]);
/**
 * Apply a single orchestration event to a `ClientOrchestrationThread`, returning
 * the updated thread, a deletion signal, or an "unchanged" marker when the
 * event doesn't affect this thread.
 *
 * This is a pure reducer operating on contract types. UI-specific mapping
 * (e.g. resolving attachment preview URLs, adding scoped fields like
 * `environmentId`) is the caller's responsibility.
 */
export function applyThreadDetailEvent(thread, event, limits = DEFAULT_THREAD_DETAIL_LIMITS) {
    switch (event.type) {
        // ── Project events (irrelevant to thread detail) ────────────────
        case "project.created":
        case "project.meta-updated":
        case "project.deleted":
            return { kind: "unchanged" };
        // ── Thread lifecycle ────────────────────────────────────────────
        case "thread.created":
            return {
                kind: "updated",
                thread: {
                    id: event.payload.threadId,
                    projectId: event.payload.projectId,
                    title: event.payload.title,
                    threadKind: event.payload.threadKind,
                    parentThreadId: event.payload.parentThreadId,
                    latestTurn: null,
                    createdAt: event.payload.createdAt,
                    updatedAt: event.payload.updatedAt,
                    archivedAt: null,
                    deletedAt: null,
                    messages: [],
                    activities: [],
                    session: null,
                },
            };
        case "thread.deleted":
            return { kind: "deleted" };
        case "thread.archived":
            return {
                kind: "updated",
                thread: {
                    ...thread,
                    archivedAt: event.payload.archivedAt,
                    updatedAt: event.payload.updatedAt,
                },
            };
        case "thread.unarchived":
            return {
                kind: "updated",
                thread: { ...thread, archivedAt: null, updatedAt: event.payload.updatedAt },
            };
        // ── Thread metadata ─────────────────────────────────────────────
        case "thread.meta-updated":
            return {
                kind: "updated",
                thread: {
                    ...thread,
                    ...(event.payload.title !== undefined ? { title: event.payload.title } : {}),
                    updatedAt: event.payload.updatedAt,
                },
            };
        // ── Turn lifecycle ──────────────────────────────────────────────
        case "thread.turn-start-requested":
            return {
                kind: "updated",
                thread: { ...thread, updatedAt: event.occurredAt },
            };
        case "thread.turn-interrupt-requested": {
            if (event.payload.turnId === undefined) {
                return { kind: "unchanged" };
            }
            const latestTurn = thread.latestTurn;
            if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
                return { kind: "unchanged" };
            }
            return {
                kind: "updated",
                thread: {
                    ...thread,
                    latestTurn: {
                        ...latestTurn,
                        state: "interrupted",
                        startedAt: latestTurn.startedAt ?? event.payload.createdAt,
                        completedAt: latestTurn.completedAt ?? event.payload.createdAt,
                    },
                    updatedAt: event.occurredAt,
                },
            };
        }
        // ── Messages ────────────────────────────────────────────────────
        case "thread.message-sent": {
            const message = {
                id: event.payload.messageId,
                role: event.payload.role,
                text: event.payload.text,
                ...(event.payload.attachments !== undefined
                    ? { attachments: event.payload.attachments }
                    : {}),
                turnId: event.payload.turnId,
                streaming: event.payload.streaming,
                createdAt: event.payload.createdAt,
                updatedAt: event.payload.updatedAt,
            };
            const existingMessage = thread.messages.find((entry) => entry.id === message.id);
            const messages = existingMessage
                ? Arr.map(thread.messages, (entry) => entry.id !== message.id
                    ? entry
                    : {
                        ...entry,
                        text: message.streaming
                            ? `${entry.text}${message.text}`
                            : message.text.length > 0
                                ? message.text
                                : entry.text,
                        streaming: message.streaming,
                        ...(message.turnId !== undefined ? { turnId: message.turnId } : {}),
                        ...(message.streaming ? {} : { updatedAt: message.updatedAt }),
                        ...(message.attachments !== undefined
                            ? { attachments: message.attachments }
                            : {}),
                    })
                : Arr.append(thread.messages, message);
            const cappedMessages = Arr.takeRight(messages, limits.maxMessages);
            // Update latestTurn for assistant messages bound to a turn.
            const latestTurn = event.payload.role === "assistant" &&
                event.payload.turnId !== null &&
                (thread.latestTurn === null || thread.latestTurn.turnId === event.payload.turnId)
                ? {
                    turnId: event.payload.turnId,
                    state: event.payload.streaming
                        ? "running"
                        : thread.latestTurn?.state === "interrupted"
                            ? "interrupted"
                            : thread.latestTurn?.state === "error"
                                ? "error"
                                : "completed",
                    requestedAt: thread.latestTurn?.turnId === event.payload.turnId
                        ? thread.latestTurn.requestedAt
                        : event.payload.createdAt,
                    startedAt: thread.latestTurn?.turnId === event.payload.turnId
                        ? (thread.latestTurn.startedAt ?? event.payload.createdAt)
                        : event.payload.createdAt,
                    completedAt: event.payload.streaming
                        ? thread.latestTurn?.turnId === event.payload.turnId
                            ? (thread.latestTurn.completedAt ?? null)
                            : null
                        : event.payload.updatedAt,
                    assistantMessageId: event.payload.messageId,
                }
                : thread.latestTurn;
            return {
                kind: "updated",
                thread: {
                    ...thread,
                    messages: cappedMessages,
                    latestTurn,
                    updatedAt: event.occurredAt,
                },
            };
        }
        // ── Session ─────────────────────────────────────────────────────
        case "thread.session-set": {
            const latestTurn = event.payload.session.status === "running" && event.payload.session.activeTurnId !== null
                ? {
                    turnId: event.payload.session.activeTurnId,
                    state: "running",
                    requestedAt: thread.latestTurn?.turnId === event.payload.session.activeTurnId
                        ? thread.latestTurn.requestedAt
                        : event.payload.session.updatedAt,
                    startedAt: thread.latestTurn?.turnId === event.payload.session.activeTurnId
                        ? (thread.latestTurn.startedAt ?? event.payload.session.updatedAt)
                        : event.payload.session.updatedAt,
                    completedAt: null,
                    assistantMessageId: thread.latestTurn?.turnId === event.payload.session.activeTurnId
                        ? thread.latestTurn.assistantMessageId
                        : null,
                }
                : thread.latestTurn;
            return {
                kind: "updated",
                thread: {
                    ...thread,
                    session: event.payload.session,
                    latestTurn,
                    updatedAt: event.occurredAt,
                },
            };
        }
        case "thread.session-stop-requested":
            return thread.session === null
                ? { kind: "unchanged" }
                : {
                    kind: "updated",
                    thread: {
                        ...thread,
                        session: {
                            ...thread.session,
                            status: "stopped",
                            activeTurnId: null,
                            updatedAt: event.payload.createdAt,
                        },
                        updatedAt: event.occurredAt,
                    },
                };
        // ── Activities ──────────────────────────────────────────────────
        case "thread.activity-appended": {
            const activities = capActivities(pipe(thread.activities, Arr.filter((activity) => activity.id !== event.payload.activity.id), Arr.append(event.payload.activity), Arr.sort(activityOrder)), limits.maxActivities);
            return {
                kind: "updated",
                thread: { ...thread, activities, updatedAt: event.occurredAt },
            };
        }
    }
    // Forward-compatible: ignore unrecognized event types.
    return { kind: "unchanged" };
}
// ── Helpers ──────────────────────────────────────────────────────────
function capActivities(sorted, maxActivities) {
    return sorted.slice(Math.max(0, sorted.length - maxActivities));
}
